// People, mounts and airships in the Inkwell style. Everything here draws in game pixels (the world transform)
// and scales itself down to ink units inside: an ink unit is a third of a game pixel (a 16 px tile is 48 units).
//
// A look (the kit recipe everyone already has) becomes an Ink character: skin, hair colour and style, the top's
// colour as the outfit, trousers, a cape for a cloak. Your job picks the class drawn: a Knight carries a spear
// and helmet, a Wizard a witch hat and staff, a Cleric a hood and white staff, a Rogue a blade.
import ink from '../ink/ink.js';
import { KIT, type KitLook } from '../../../shared/kit.js';
import { MOUNTS } from '../../../shared/mounts.js';
import { APPEARANCE } from '../../../shared/races.js';

const I = ink as any;
const { INK, TAU, cFuzz, oval, line, poly, blob } = I;
export const K = 3;
const FEM = new Set(['long', 'bob', 'braids', 'ponytail']);
const HAIR_STYLE: Record<string, string> = { short: 'short', long: 'long', bob: 'bob', cowlick: 'messy', braids: 'braid', ponytail: 'ponytail', receding: 'buzz' };
/** Townsfolk whose outfit says what they do. */
const NPC_CLS: Record<string, string> = { 'Captain Rook': 'knight', 'Apprentice Juno': 'blackmage', 'Sister Tamsin': 'whitemage', 'Fen the Quick': 'rogue', 'Big Bo': 'brawler', 'Drillmaster Pell': 'knight', 'Ranger Hollis': 'archer', 'Ser Brannoc': 'knight', 'Guildmaster Oak': 'knight', 'Gatewarden Hale': 'knight', 'Magister Fenwick': 'blackmage', 'Stargazer Wren': 'blackmage',
  'Sister Maren': 'whitemage', 'Weaver Lin': 'whitemage', 'Nim the Quick': 'rogue', 'Smith Bruna': 'swordsman' };
const colour = (v: string | undefined, table: Record<string, string>, dflt: string) => (v && table[v]) ?? (v?.startsWith('#') ? v : dflt);

export interface InkDef { name: string; cls: string; gender: string; body: string; height: number; mood: string; hairStyle: string; hair: string; skin: string; outfit: string; pants: string; cape: string | null;
  race: string; facial: string; jewelry: string; markings: string; showHeadgear?: boolean;
  /** From gear (players): the hat and weapon drawn (null / 'none': nothing), and the hat's colour. */
  hat?: string | null; weapon?: string; capColor?: string; ribbon?: string }
/** Players look like their gear, FFXIV style: the body piece picks the outfit, the head piece the hat, the main
 *  hand the weapon. Nothing worn: plain adventurer clothes, empty-handed. */
const BODY_CLS: [string, string][] = [['plate', 'knight'], ['robe_black', 'blackmage'], ['robe_white', 'whitemage'], ['leathers', 'rogue'], ['gi', 'brawler']];
const HEAD_HAT: Record<string, string> = { spikehelm: 'helmet', wizard: 'witch', hood: 'hood', bighat: 'straw', beanie: 'cap', kepi: 'cap', bowler: 'cap', slouch: 'cap', toque: 'cap', bicorne: 'cap' };
const MAIN_WEAPON: Record<string, string> = { sword: 'sword', woodsword: 'sword', staff: 'staffB', wand: 'staffW', spear: 'spear', bow: 'bow' };
function gearLook(wear: string[]): { cls: string; hat: string | null; weapon: string } {
  return { cls: BODY_CLS.find(([w]) => wear.includes(w))?.[1] ?? 'villager', hat: wear.map(w => HEAD_HAT[w]).find(Boolean) ?? null, weapon: wear.map(w => MAIN_WEAPON[w]).find(Boolean) ?? 'none' };
}
/** Looks from before races: the kit's face hair as a human's. */
const OLD_FACE: Record<string, string> = { mustache: 'mustache', whiskers: 'stubble', muttonchops: 'full' };

/** The Ink character for a look. `job` decides the class for players; townsfolk go by name and what they carry. */
export function defFor(look: KitLook | null | undefined, o: { job?: string; name?: string; npc?: boolean; tool?: string } = {}): InkDef {
  const L = look ?? ({} as KitLook), wear = L.wear ?? [], style = L.style ?? 'short', seed = I.strSeed(o.name ?? '');
  const player = !o.npc && o.job !== undefined, gear = player ? gearLook(wear) : null;
  let cls = gear ? gear.cls : undefined;
  if (!cls && o.npc && o.name) cls = NPC_CLS[o.name];
  if (!cls) cls = wear.includes('wizard') || wear.includes('staff') ? 'blackmage' : wear.includes('hood') ? 'whitemage' : wear.includes('shield') || wear.includes('spikehelm') ? 'knight'
    : wear.includes('sword') || wear.includes('woodsword') ? 'swordsman' : 'villager';
  let body = 'adult';
  if (o.npc) { if (L.hair === 'gray' || (L.hair === 'silver' && style === 'receding')) body = 'elder'; else if (wear.includes('apron') && seed % 2 === 0) body = 'stout'; }
  const race = L.race && APPEARANCE[L.race] ? L.race : 'human', app = APPEARANCE[race];
  const ihair = L.race && L.ihair && L.ihair in app.hair ? L.ihair : null;
  const oldFace = wear.map(w => OLD_FACE[w]).find(Boolean);
  return { name: o.name ?? '', cls, gender: L.frame ?? (FEM.has(style) || wear.includes('dress') ? 'f' : 'm'), body: L.build ?? body, height: L.height ?? (o.npc ? 0.92 + (seed % 17) / 100 : 1),
    mood: L.mood ?? (o.npc ? ['neutral', 'happy', 'neutral', 'sleepy', 'happy'][seed % 5] : 'neutral'),
    hairStyle: ihair ?? HAIR_STYLE[style] ?? 'short', hair: colour(L.hair, KIT.HAIRS, '#8a5636'), skin: colour(L.skin, KIT.SKINS, '#f8d3ad'),
    outfit: wear.includes('dress') && L.dress ? L.dress : colour(L.top, KIT.TOPS, '#8cc4ec'), pants: colour(L.bottom, KIT.BOTTOMS, '#4c5a80'),
    cape: wear.includes('cloak') && L.cloak ? L.cloak : null,
    race, facial: L.race ? (L.facial && L.facial in app.face ? L.facial : 'none') : (oldFace ?? 'none'),
    jewelry: L.jewel && L.jewel in app.jewel ? L.jewel : 'none', markings: L.mark && L.mark in app.mark ? L.mark : 'none',
    ...(L.headgear === false ? { showHeadgear: false } : {}),
    ...(gear ? { hat: gear.hat, weapon: gear.weapon, capColor: L.cap, ribbon: L.ribbon } : {}),
    ...(o.tool ? { weapon: o.tool } : {}) };   // townsfolk: the tool in hand (a hammer, a saw, an umbrella in the rain)
}

/** Which way a direction faces, as an angle on the ground (0 right, down is towards you). */
export const DIR_TH = [Math.PI / 2, 0, -Math.PI / 2, Math.PI];
const SNAP = (a: number) => Math.round(a / (Math.PI / 4)) * (Math.PI / 4);
const EXPR: Record<string, string> = { smile: 'happy', happy: 'happy', laugh: 'happy', wink: 'happy', excited: 'surprised', surprised: 'surprised', sad: 'sad', angry: 'angry', worried: 'worried', sleepy: 'sleepy', determined: 'determined', neutral: 'neutral' };

interface Person { c: any; x: number; y: number; dir: number; attacking: boolean; seen: number; key: string }
/** Everyone's Ink character, kept between frames so walking, turning and swinging animate smoothly. */
export class People {
  readonly all = new Map<number | string, Person>();
  /** Move someone to (x, y) facing `dir` and advance their animation; returns their character. */
  step(id: number | string, look: KitLook | null | undefined, o: { job?: string; name?: string; npc?: boolean; tool?: string; work?: string | null }, x: number, y: number, dir: number, attacking: boolean, dt: number, now: number, face?: string | null, still = false): any {
    const who = JSON.stringify(look ?? null) + (o.job ?? '') + (o.npc ? o.name : ''), key = who + (o.tool ?? '');
    let p = this.all.get(id);
    if (!p || p.key !== key) {
      const c = I.makeChar(defFor(look, o), x * K, y * K, DIR_TH[dir] ?? DIR_TH[0]);
      if (p && p.key.startsWith(who)) { c.th = p.c.th; c.thT = p.c.thT; }   // the same person (a new outfit or tool): keep facing the same way; someone else: their own way
      p = { c, x, y, dir, attacking: false, seen: now, key }; this.all.set(id, p);
    }
    p.c.work = o.work ?? null;   // townsfolk at work: a looped pose while they stand still
    const c = p.c, dx = x - p.x, dy = y - p.y, d = Math.hypot(dx, dy), moved = d > 24 ? 0 : d;   // a jump across the map (a door): no running animation
    if (moved > 0 && moved / Math.max(dt, 1e-3) > 6) c.thT = SNAP(Math.atan2(dy, dx));
    else if (dir !== p.dir) c.thT = DIR_TH[dir] ?? c.thT;
    if (attacking && !p.attacking) { c.thT = DIR_TH[dir] ?? c.thT; c.th = c.thT; I.startAction(c, 'attack'); }
    p.attacking = attacking; p.dir = dir; p.x = x; p.y = y; p.seen = now;
    if (face) I.setExpr(c, EXPR[face] ?? 'neutral', 0.3);
    I.updateChar(c, dt, still ? 0 : moved * K, now / 1000);   // riding: legs still
    c.x = x * K; c.y = y * K;
    return c;
  }
  /** Forget people not drawn for a while. */
  sweep(now: number): void { for (const [k, p] of this.all) if (now - p.seen > 4000) this.all.delete(k); }
  /** Face someone (talking to them). */
  face(id: number | string, x: number, y: number): void { const p = this.all.get(id); if (p) p.c.thT = Math.atan2(y - p.y, x - p.x); }
}

/** The height of someone's head above their feet, in game pixels. */
export const personTop = (c: any, k = 1) => I.charTop(c.def, I.S) / K * k;

/** A soft shadow on the ground, `w` game pixels wide, fainter when the thing above is high up. */
export function shadow(g: CanvasRenderingContext2D, x: number, y: number, w: number, lift = 0): void {
  const k = Math.max(0.45, 1 - lift / 60);
  g.fillStyle = `rgba(62,47,40,${0.2 * k})`; g.beginPath(); g.ellipse(x, y + 0.5, w * k / 2, w * k / 6, 0, 0, TAU); g.fill();
}

/** Draw a person with their feet at (x, y), `lift` game pixels up (jumping, riding). `k` scales them (the world map). */
export function drawPerson(g: CanvasRenderingContext2D, c: any, x: number, y: number, lift: number, k = 1, now = performance.now(), noShadow = false): void {
  if (!noShadow) shadow(g, x, y, 9 * k, lift);
  g.save(); g.translate(x, y - lift); g.scale(k / K, k / K);
  I.drawChar(g, c, 0, 0, I.S, now / 1000);
  g.restore();
}

// ---------- mounts: woolly cloud-sheep, one palette per breed ----------
/** How high a rider sits on a mount, in game pixels. */
export const SEAT = 8;
/** Draw a mount (feet at x, y, `lift` up). `layer`: 'back' goes under the rider (shadow, a head facing away),
 *  'front' over them (the woolly body around their legs, a head facing you). */
export function drawMount(g: CanvasRenderingContext2D, kind: number, x: number, y: number, lift: number, th: number, flying: boolean, moving: boolean, now: number, layer: 'back' | 'front', seed = 0, k = 1): void {
  const m = MOUNTS.find(q => q.kind === kind) ?? MOUNTS[0], s = m.scale * k * 1.45, t = now / 1000 + seed * 0.37;
  const Fx = Math.cos(th), Fy = Math.sin(th), away = Fy < -0.35, bob = flying ? Math.sin(t * 2.4) * 1.5 : moving ? -Math.abs(Math.sin(t * 9)) * 1.2 : 0;
  if (layer === 'back') shadow(g, x, y, 15 * s, lift);
  g.save(); g.translate(x, y - lift + bob); g.scale(s / K, s / K);
  const head = () => {
    const hx = Fx * 19, hy = -16 + Fy * 6 - (away ? 4 : 0);
    if (Math.abs(Fx) > 0.3 || !away) {
      [-1, 1].forEach(e => oval(g, hx + e * 8 - Fx * 2, hy - 7, 5, 3, m.face, 9100 + e, 1.6, 8, e * 0.6));   // ears
      oval(g, hx, hy, 9, 8, m.face, 9103, 1.8, 10);
      if (!away) { g.fillStyle = '#fffaf0'; [-1, 1].forEach(e => { if (Fx * e < 0.5) { g.beginPath(); g.arc(hx + e * 3.4 + Fx * 3, hy - 1, 1.6, 0, TAU); g.fill(); } }); }
      cFuzz(g, hx - Fx * 1, hy - 7, 5, 2.5, 5, 3.4, m.wool[0], 9110, 1.6);   // a tuft between the ears
    }
  };
  if (layer === 'back') {
    if (!flying) [[-9, 1], [9, 1], [-5, 4], [5, 4]].forEach(([lx, ly], i) => { const sw = moving ? Math.sin(t * 9 + i * 1.7) * 2 : 0; line(g, [[lx + sw, -8 + ly], [lx + sw * 1.4, -1 + ly]], 9120 + i, 3.4, INK); line(g, [[lx + sw, -8 + ly], [lx + sw * 1.4, -1.6 + ly]], 9130 + i, 2, m.face); });
    if (away) head();
  } else {
    cFuzz(g, -Fx * 2, -14, 19, 10, 9, 6.5, m.wool[0], 9140, 2);
    g.fillStyle = m.wool[2]; g.globalAlpha = 0.55; g.beginPath(); g.ellipse(-Fx * 2, -8, 16, 4.5, 0, 0, Math.PI); g.fill(); g.globalAlpha = 1;
    g.fillStyle = 'rgba(255,255,255,0.6)'; g.beginPath(); g.ellipse(-8 - Fx * 2, -20, 6, 2.5, -0.3, 0, TAU); g.fill();
    if (flying) for (let i = 0; i < 3; i++) { const ph = (t * 0.8 + i / 3) % 1; g.globalAlpha = (1 - ph) * 0.8; cFuzz(g, -Fx * (22 + ph * 22), -8 + ph * 4, 5 * (1 - ph * 0.5), 3, 5, 3, m.wool[1], 9150 + i, 1.4); g.globalAlpha = 1; }
    if (!away) head();
    if (kind === 4) { g.globalCompositeOperation = 'lighter'; I.cGlow(g, 0, -14, 30, '#c9b8f0', 0.18); g.globalCompositeOperation = 'source-over'; }   // Gloamwool glows
  }
  g.restore();
}

// ---------- the airship ----------
/** How high an airship flies above its shadow, in game pixels. */
export const SHIP_LIFT = 34;
/** Draw the airship: 'back' is the shadow, the balloon and the far rail; 'front' is the hull over the crew's legs.
 *  Returns where the deck is (feet of whoever's aboard), in game pixels. */
export function drawAirship(g: CanvasRenderingContext2D, x: number, y: number, lift: number, dir: number, now: number, layer: 'back' | 'front', k = 1): { deckY: number } {
  const t = now / 1000, bob = Math.sin(t * 1.6) * 1.5 * k, face = dir === 3 ? -1 : 1, top = y - lift * k + bob;
  if (layer === 'back') shadow(g, x, y, 46 * k, 0);
  g.save(); g.translate(x, top); g.scale(face * k / K, k / K);
  if (layer === 'back') {
    // ropes, then the striped balloon
    [-30, -10, 10, 30].forEach((rx, i) => line(g, [[rx, -14], [rx * 1.6, -78]], 9200 + i, 1.4, 'rgba(62,47,40,0.7)'));
    oval(g, 0, -118, 78, 46, '#f4efe4', 9210, 2.4, 18);
    g.save(); g.beginPath(); g.ellipse(0, -118, 76, 44, 0, 0, TAU); g.clip();
    g.fillStyle = '#e3604b'; for (let sx = -70; sx < 80; sx += 36) { g.beginPath(); g.ellipse(sx, -118, 9, 46, 0, 0, TAU); g.fill(); }
    g.fillStyle = 'rgba(255,255,255,0.35)'; g.beginPath(); g.ellipse(-26, -140, 26, 10, -0.2, 0, TAU); g.fill();
    g.restore();
    g.lineWidth = 2.4; g.strokeStyle = INK; g.beginPath(); g.ellipse(0, -118, 78, 46, 0, 0, TAU); g.stroke();
    poly(g, [[-16, -74], [16, -74], [10, -66], [-10, -66]], '#c79a6a', 9220, 2);
    // the propeller at the back
    const sp = Math.sin(t * 30);
    line(g, [[-58, -12], [-70, -12]], 9230, 2.4, INK);
    oval(g, -72, -12, 3, 12 * Math.abs(sp) + 2, '#d9a94e', 9231, 1.6, 8);
    // the far rail
    line(g, [[-44, -26], [46, -26]], 9240, 2.2, '#9b7653');
  } else {
    // the hull: a round wooden boat with a rail and a little lantern at the bow
    blob(g, [[-58, -24], [56, -24], [64, -30], [52, -6], [30, 2], [-36, 2], [-54, -8]], '#c79a6a', 9250, 2.4, 0.4);
    for (let i = 0; i < 3; i++) line(g, [[-50 + i * 4, -16 + i * 6], [52 - i * 6, -16 + i * 6]], 9260 + i, 1.4, 'rgba(62,47,40,0.45)');
    line(g, [[-58, -24], [60, -24]], 9270, 2.6, INK);
    [-40, -14, 12, 38].forEach((px, i) => line(g, [[px, -24], [px, -34]], 9280 + i, 2, '#9b7653'));
    line(g, [[-44, -34], [46, -34]], 9290, 2.4, '#9b7653');
    oval(g, 62, -38, 4, 5, '#f7d35e', 9295, 1.6, 8); I.cGlow(g, 62, -38, 18, '#f7d35e', 0.35);
  }
  g.restore();
  return { deckY: top - 12 * k / K };
}

const SNAPS = new Map<number | string, HTMLCanvasElement>();
/** Someone drawn into a picture of their own (for the sightseeing camera's billboards): 4 pixels per game pixel,
 *  feet at the bottom middle. */
export function personPicture(key: number | string, c: any, now: number): { img: HTMLCanvasElement; ax: number; scale: number } {
  let cv = SNAPS.get(key);
  if (!cv) { cv = document.createElement('canvas'); cv.width = 160; cv.height = 150; SNAPS.set(key, cv); if (SNAPS.size > 80) SNAPS.delete(SNAPS.keys().next().value!); }
  const g = cv.getContext('2d')!; g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, cv.width, cv.height);
  g.translate(80, 146); g.scale(4 / K, 4 / K); I.drawChar(g, c, 0, 0, I.S, now / 1000);
  return { img: cv, ax: 80, scale: 4 };
}

/** Someone far off in a crowd, drawn simply (a body and a head in their colours): much cheaper than a full character. */
export function drawCheap(g: CanvasRenderingContext2D, c: any, x: number, y: number, lift: number, k = 1): void {
  const d = c.def, bob = Math.abs(Math.sin(c.phase * Math.PI * 2)) * 2;
  shadow(g, x, y, 9 * k, lift);
  g.save(); g.translate(x, y - lift); g.scale(k / K, k / K);
  oval(g, 0, -16 - bob, 10, 14, d.outfit, c.uid * 7, 1.8, 10);
  oval(g, 0, -48 - bob, 17, 16, d.skin, c.uid * 7 + 1, 1.8, 12);
  g.fillStyle = d.hair; g.beginPath(); g.ellipse(0, -52 - bob, 16, 11, 0, Math.PI, TAU); g.fill(); g.lineWidth = 1.8; g.strokeStyle = INK; g.stroke();
  g.restore();
}

const STILL = new People();
/** Paint someone standing, whole body, into a small canvas (character select, the creator, the Me window). The
 *  canvas is given more pixels the first time so it stays sharp. `dir`: down, right, up or left. */
export function paintLook(cv: HTMLCanvasElement, look: KitLook | null | undefined, o: { job?: string; dir?: string; key?: string; bg?: string } = {}): void {
  if (!cv.dataset.ink) { cv.width *= 4; cv.height *= 4; cv.dataset.ink = '1'; }
  const g = cv.getContext('2d')!, d = ['down', 'right', 'up', 'left'].indexOf(o.dir ?? 'down');
  g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, cv.width, cv.height);
  if (o.bg) { g.fillStyle = o.bg; g.fillRect(0, 0, cv.width, cv.height); }
  const c = STILL.step(o.key ?? JSON.stringify(look) + (o.job ?? ''), look, { job: o.job }, 0, 0, Math.max(0, d), false, 1 / 60, performance.now(), 'happy', true);
  c.th = c.thT = DIR_TH[Math.max(0, d)] + (d === 0 ? 0.35 : 0); c.def.mood = 'happy';
  const k = cv.height * 0.8 / (I.charTop(c.def, I.S) || 70);
  g.translate(cv.width / 2, cv.height * 0.93);
  g.fillStyle = 'rgba(62,47,40,0.18)'; g.beginPath(); g.ellipse(0, 0, 16 * k, 5 * k, 0, 0, TAU); g.fill();
  g.scale(k, k); I.drawChar(g, c, 0, 0, I.S, performance.now() / 1000);
}
